import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { LineChart } from "@mui/x-charts/LineChart";
import AreaGradient from "../area-gradient";
import { Grid, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { UserStatisticResponse } from "src/_pages/admin/interfaces/user-statical-interface";

interface Props {
  data: UserStatisticResponse | null;
  loading: boolean;
}

const UsersChart = ({ data, loading }: Props) => {
  const colorPalette = ["green", "red"];

  const xAxisData =
    data?.details?.map((d) =>
      dayjs(d.date).format("DD/MM/YYYY")
    ) ?? [];

  const newUsersData =
    data?.details?.map((d) => d.newUsers) ?? [];

  const deletedUsersData =
    data?.details?.map((d) => d.deletedUsers) ?? [];

  const newGrowth = data?.newUsersGrowthPercent ?? 0;
  const deletedGrowth = data?.deletedUsersGrowthPercent ?? 0;

  const isNewGrowthPositive = newGrowth >= 0;
  const isDeletedGrowthPositive = deletedGrowth >= 0;

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardContent>
        <Grid container spacing={2} columns={12} marginBottom={2}>
          <Grid item xs={6}>
            <Typography variant="subtitle2">
              Người dùng mới
            </Typography>

            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="h4">
                {data?.totalNewUsers?.toLocaleString() ?? 0}
              </Typography>
              <Chip
                size="small"
                color={isNewGrowthPositive ? "success" : "error"}
                label={`${isNewGrowthPositive ? "+" : ""}${newGrowth}%`}
              />
            </Stack>

            {data?.previousPeriod && (
              <Tooltip
                title={`Kỳ trước (${dayjs(
                  data.previousPeriod.from
                ).format("DD/MM/YYYY")} - ${dayjs(
                  data.previousPeriod.to
                ).format("DD/MM/YYYY")}): ${
                  data.previousPeriod.totalNewUsers
                }`}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ cursor: "pointer" }}
                >
                  So với kỳ trước
                </Typography>
              </Tooltip>
            )}
          </Grid>

          <Grid item xs={6}>
            <Typography variant="subtitle2">
              Xóa người dùng
            </Typography>

            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="h4">
                {data?.totalDeletedUsers?.toLocaleString() ?? 0}
              </Typography>

              <Chip
                size="small"
                color={isDeletedGrowthPositive ? "error" : "success"}
                label={`${isDeletedGrowthPositive ? "+" : ""}${deletedGrowth}%`}
              />
            </Stack>

            {data?.previousPeriod && (
              <Tooltip
                title={`Kỳ trước (${dayjs(
                  data.previousPeriod.from
                ).format("DD/MM/YYYY")} - ${dayjs(
                  data.previousPeriod.to
                ).format("DD/MM/YYYY")}): ${
                  data.previousPeriod.totalDeletedUsers
                }`}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ cursor: "pointer" }}
                >
                  So với kỳ trước
                </Typography>
              </Tooltip>
            )}
          </Grid>
        </Grid>

        <LineChart
          colors={colorPalette}
          xAxis={[
            {
              scaleType: "point",
              data: xAxisData,
            },
          ]}
          yAxis={[]}
          series={[
            {
              id: "newUsers",
              label: "Người dùng mới",
              showMark: true,
              curve: "linear",
              stack: "total",
              area: true,
              stackOrder: "ascending",
              data: newUsersData,
            },
            {
              id: "deletedUsers",
              label: "Người dùng xóa",
              showMark: true,
              curve: "linear",
              stack: "total",
              area: true,
              stackOrder: "ascending",
              data: deletedUsersData,
            },
          ]}
          height={250}
          margin={{ left: 25, right: 20, top: 50, bottom: 40 }}
          sx={{
            "& .MuiAreaElement-series-newUsers": {
              fill: "url('#newUsers')",
            },
            "& .MuiAreaElement-series-deletedUsers": {
              fill: "url('#deletedUsers')",
            },
            "& .MuiChartsAxis-tickLabel": {
              fontSize: 11,
            },
          }}
        >
          <AreaGradient color="green" id="newUsers" />
          <AreaGradient color="red" id="deletedUsers" />
        </LineChart>
      </CardContent>
    </Card>
  );
};

export default UsersChart;
